import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { authApi } from '@/shared/api/apiEndpoints'
import type { AuthUser, LoginRequest } from '@/types'

interface AuthState {
  user: AuthUser | null
  token: string | null
  isLoading: boolean
}

interface AuthContextValue extends AuthState {
  login: (data: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
  canWrite: boolean      // userLevel <= 4
  isAdmin: boolean       // userLevel === 0
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('token'),
    isLoading: true,
  })

  // 페이지 새로고침 시 토큰으로 사용자 복원
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      setState((s) => ({ ...s, isLoading: false }))
      return
    }
    authApi
      .me()
      .then((user) => setState({ user, token, isLoading: false }))
      .catch(() => {
        localStorage.removeItem('token')
        setState({ user: null, token: null, isLoading: false })
      })
  }, [])

  const login = useCallback(async (data: LoginRequest) => {
    const res = await authApi.login(data) as unknown as { token?: string; user?: AuthUser; resultMessage?: string }
    if (!res.token) {
      throw new Error(res.resultMessage ?? '아이디 또는 비밀번호가 올바르지 않습니다.')
    }
    localStorage.setItem('token', res.token)
    setState({ user: res.user!, token: res.token, isLoading: false })
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => {})
    localStorage.removeItem('token')
    setState({ user: null, token: null, isLoading: false })
  }, [])

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    isAuthenticated: !!state.user,
    canWrite: !!state.user && state.user.userLevel <= 4,
    isAdmin: !!state.user && state.user.userLevel === 0,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('AuthProvider가 없습니다.')
  return ctx
}
